import React, { useState } from 'react';

import CAutocomplete from '../../../components/CAutocomplete';
import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import CModal from '../../../components/CModal';
import CPageModule from '../../../components/CPageModule';
import CPandasTable from '../../../components/CPandasTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { updateStateAttribute } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import type { IOptionsCalculatorConfig } from '../../../models/IOptionsCalculatorConfig';
import type { IResponse } from '../../../models/IResponse';
import type { IResponseTableOrPlot } from '../../../models/IResponseTableOrPlot';
import type { TPandasTable } from '../../../models/TPandasTable';
import { type PCalculatorIndexState } from './PCalculatorIndex';
import formatTableColumn from '../../../helpers/formatTableColumn';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import CInfoButtonWithModal from '../../../components/CInfoButtonWithModal';

interface Props {
  optionsConfigDefault: IOptionsCalculatorConfig;
  optionsTable: IResponseTableOrPlot<TPandasTable>;
  optionsConfigInput: IOptionsCalculatorConfig;
  onUpdateStateAttribute: (partialState: Partial<PCalculatorIndexState>) => void;
  optionsTableDerivatives: IResponse<{ options: Array<{label: string, value: string }>}>;
}

interface State {
  showModal: boolean;
}

export default function CCalculatorTable(props: Props) {
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const [state, setState] = useState<State>({ showModal: false });

  const cLocalizedTextStringDerivatives = CLocalizedTextString('topics.calculator.tables.derivatives');

  const formattedData = formatTableColumn(props.optionsTable?.data, 3, (item, row) =>
    <CInfoButtonWithModal title={row[0]}>
      <Markdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>{item}</Markdown>
    </CInfoButtonWithModal>);

  return (<CPageModule>
      <h3>
        <CLocalizedText dictKey={'topics.calculator.tables.heading'} />
      </h3>
      <CStatefulContainer showContent={!!props.optionsTableDerivatives.data}
                          apiRequestState={props.optionsTableDerivatives.state}>
        <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
          <CAutocomplete
            value={props.optionsConfigInput.derivatives?.map((e) => {
              return { label: e, key: e };
            }) ?? []}
            onChange={async (options) => {
              props.onUpdateStateAttribute({
                optionsConfigInput: {
                  ...props.optionsConfigInput,
                  derivatives: options.length === 0 ? props.optionsConfigDefault.derivatives : options?.map((e) => e.key)
                }
              });
            }}
            textFieldLabel={cLocalizedTextStringDerivatives}
            multiple
            options={props.optionsTableDerivatives.data?.options.map((e) => {
              return { label: e.label, key: e.value };
            }) ?? []}
          />

          <div
            className={'grid grid-cols-2 gap-x-12 gap-y-4'}
            style={{
              gridTemplateColumns: dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_3 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(1, minmax(0, 1fr))'
            }}
          >
            <CStatefulContainer apiRequestState={props.optionsTable.state}>
              <CPandasTable
                data={formattedData}
                style={{ minWidth: 'auto' }}
                colAlignmentArr={['left', 'right', 'right', 'center']}
                colSpanArr={[2, 1, 1, 1]}
                colFormatArr={[{ in: 'markdownString', out: 'markdown' }, undefined, undefined, undefined]}
              />
            </CStatefulContainer>

            <div
              style={{
                paddingTop: dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_2 ? '0.75rem' : 'none'
              }}
            >
              <CLocalizedText
                dictKey={'calculatorTableDesc.preview'}
                onClickReadMore={() => {
                  updateStateAttribute<State>({ showModal: true }, setState);
                }}
                collapsible
                wordLength={100} // becuase default word length is 50
              />
              <CModal
                open={state.showModal}
                onClose={() => {
                  updateStateAttribute<State>({ showModal: false }, setState);
                }}
              >
                <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <CLocalizedText dictKey={'calculatorTableDesc'} />
                </CPageModule>
              </CModal>
            </div>
          </div>
        </CPageModule>
      </CStatefulContainer>
    </CPageModule>);
}
